<script>
  import { apiMethods } from "../../../Services/index.svelte";
  import {
    getFormatDateAndTime,
    handleDownloadPDF,
    isObjectEmpty,
    showError,
    showException,
    showInfo,
  } from "../../../Utils/utilFunctions.svelte";
  import NotificationModal from "../../../Components/Common/NotificationModal.svelte";
  import Loader from "../../../Components/Common/Loader.svelte";
  import { downLoadIcon } from "../../../Utils/utilConstants.svelte";

  export let isModalOpen = false;
  export let encounterId, encounterDoctorId;

  $: if (isUploadOCRModalOpen) setTimeout(() => enableFileDragAndDrop(), 0);
  else disableFileDragAndDrop();

  $: isUploadButtonDisabled = isObjectEmpty(fileObj) || isUploadingFile;

  let isGettingPatientOcrList = false,
    allFiles = [],
    isUploadingFile = false,
    fileInput,
    fileObj = {},
    isDownloadingOnlyFile = false,
    isUploadOCRModalOpen = false;

  const init = () => loadEncounterLists(true);

  const loadEncounterLists = async (firstLoad) => {
    allFiles = [];
    isGettingPatientOcrList = true;

    await apiMethods.getMethod
      .getOcrList(0, 999999, {
        "filter[doctorId]": encounterDoctorId,
        "filter[patientEncounterId]": encounterId,
      })
      .then(async (res) => {
        if (!res || res.error)
          throw (
            res.error ??
            "Failed to load OCR list, please refresh the page and try again and contact support if the issue persists."
          );
        else if (res.results?.length) {
          const filteredFiles = [];
          res.results.forEach((file) => {
            if (
              !filteredFiles.some(
                (filteredFile) => filteredFile.pdfId === file.pdfId,
              )
            )
              filteredFiles.push(file);
          });
          if (filteredFiles.length)
            filteredFiles.sort(
              (a, b) =>
                new Date(b.createdDate).getTime() -
                new Date(a.createdDate).getTime(),
            );

          if (filteredFiles.length === 1 && firstLoad) {
            isDownloadingOnlyFile = true;
            await downloadFile(filteredFiles[0]);
            isDownloadingOnlyFile = false;
            isModalOpen = false;
          } else allFiles = filteredFiles;
        }
      })
      .catch((err) => showError(err));

    isGettingPatientOcrList = false;
  };

  const onFileChange = (e, isDropFile = false) => {
    if (isUploadingFile) return;

    const inputField = document.getElementById("fileNamePlaceholder");

    const file = isDropFile ? e.dataTransfer.files[0] : e.target.files[0];

    const fileType = file?.type;

    if (
      !fileType ||
      (fileType !== "application/pdf" &&
        fileType !== "image/jpeg" &&
        fileType !== "image/png")
    )
      return;

    inputField.innerText = file.name;

    const fileName = file.name;

    let reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (e) => {
      fileObj = {
        name: fileName,
        type: fileType,
        content: e.target.result.split(",")[1],
      };
    };
  };

  const enableFileDragAndDrop = () => {
    const enableDropArea = document.getElementById("enableDropArea");
    const uploadDoctorPdfModal = document.getElementById(
      "uploadDoctorPdfModal",
    );

    uploadDoctorPdfModal.addEventListener("dragover", function (e) {
      e.preventDefault();

      enableDropArea.classList.add("outline-dashed");
      enableDropArea.classList.add("outline-secondary");
      enableDropArea.classList.add("outline-2");
    });

    uploadDoctorPdfModal.addEventListener("dragleave", function (e) {
      enableDropArea.classList.remove("outline-dashed");
      enableDropArea.classList.remove("outline-secondary");
      enableDropArea.classList.remove("outline-2");
    });

    uploadDoctorPdfModal.addEventListener("drop", function (e) {
      e.preventDefault();

      enableDropArea.classList.remove("outline-dashed");
      enableDropArea.classList.remove("outline-secondary");
      enableDropArea.classList.remove("outline-2");
    });

    enableDropArea.addEventListener("drop", function (e) {
      e.preventDefault();

      if (isUploadingFile) return;

      onFileChange(e, true);

      this.classList.remove("outline-dashed");
      this.classList.remove("outline-secondary");
      this.classList.remove("outline-2");
    });
  };

  const disableFileDragAndDrop = () => {
    const enableDropArea = document.getElementById("enableDropArea");

    enableDropArea && enableDropArea.removeEventListener("drop", onFileChange);
  };

  const openUploadStickerModal = () => (isUploadOCRModalOpen = true);

  const downloadFile = async (file) => {
    if (!file.pdfId) return showError("File ID Missing.");
    await apiMethods.getMethod
      .getAccountSchedulePDF(file.pdfId)
      .then((res) => {
        if (!res.pdfContent)
          throw "Failed to load the schedule PDF content, please refresh the page and try again and contact support if the issue persists.";
        else handleDownloadPDF(res.pdfContent, "Account Schedule", true);
      })
      .catch((err) => {
        showError(err);
      });
  };

  const uploadFile = async () => {
    isUploadingFile = true;

    const pdfObj = {
      accountId: encounterDoctorId,
      inputBase64: fileObj.content,
    };

    await apiMethods.putMethod
      .addListToEncounter(pdfObj, encounterId)
      .then((res) => {
        if (res) {
          showInfo("List Upload");
          closeUploadModal();
          loadEncounterLists();
        } else throw "error";
      })
      .catch(() => showException());

    clearFileInput();

    isUploadingFile = false;
  };

  const clearFileInput = () => {
    fileObj = {};

    const inputField = document.getElementById("fileNamePlaceholder");
    if (inputField) inputField.innerText = "";

    const fileInput = document.getElementById("fileInput");
    if (fileInput) fileInput.value = "";
  };

  const closeUploadModal = () => {
    clearFileInput();
    isUploadOCRModalOpen = false;
  };
</script>

<NotificationModal
  {isModalOpen}
  title="Encounter OCR List"
  showConfirmBtn={false}
  cancelText="Close"
  closeModalFunction={() => (isModalOpen = false)}
  closeModal={() => (isModalOpen = false)}
  headerOptions={[
    {
      isHidden: false,
      iFont: "fa-solid fa-upload",
      iconName: "UploadListIcon",
      optionText: "Attach List",
      showAsTooltip: true,
      function: () => openUploadStickerModal(),
    },
  ]}
>
  <div class="flex flex-col flex-1 gap-y-4 overflow-y-auto" use:init>
    {#if isGettingPatientOcrList}
      <p>Loading Encounter Lists...</p>
      <Loader />
    {:else if isDownloadingOnlyFile}
      <p>Downloading Encounter List File...</p>
      <Loader />
    {:else if allFiles.length}
      <div class="flex-1 flex-col overflow-y-scroll max-height-50vh">
        <div
          class="col-span-4 grid grid-cols-4 gap-3 files-row items-center sticky py-2"
        >
          <span class="col-span-3">Created Date</span>
          <span class="col-span-1" />
        </div>
        {#each allFiles as file}
          <div
            class="col-span-4 grid grid-cols-4 gap-3 files-row items-end py-2"
          >
            <span class="col-span-3">
              {getFormatDateAndTime(file.createdDate)}
            </span>
            <div class="col-span-1 flex flex-1 justify-end">
              <button
                class="btn btn-sm btn-circle btn-ghost"
                on:click={() => downloadFile(file)}
              >
                <i class={downLoadIcon} />
              </button>
            </div>
          </div>
        {/each}
      </div>
    {:else}
      <div class="flex flex-1 items-start gap-y-4 flex-col">
        <p>No lists found for this encounter</p>
      </div>
    {/if}
  </div>
</NotificationModal>

<NotificationModal
  isModalOpen={isUploadOCRModalOpen}
  title="Upload List PDF"
  showCancelBtn={false}
  showConfirmBtn={false}
  closeModalFunction={closeUploadModal}
  isConfirming={isUploadingFile}
  customizeId="uploadDoctorPdfModal"
  btnOptions={[
    {
      text: "Cancel",
      class: "btn btn-accent",
      loading: isUploadingFile,
      function: () => closeUploadModal(),
    },
    {
      text: isUploadingFile ? "Uploading" : "Upload",
      loading: isUploadingFile || isUploadButtonDisabled,
      function: () => uploadFile(),
    },
  ]}
>
  <div class="flex flex-col gap-y-4 flex-1" id="enableDropArea">
    {#if isUploadingFile}
      <Loader />
    {:else}
      <div class="flex flex-1 text-[#000]">
        <p
          id="fileNamePlaceholder"
          class="w-1/2 pt-2 pl-2 border-2 rounded-tl-lg rounded-bl-lg text-secondary"
        />
        <button
          class="w-1/2 p-2 rounded-tr-lg rounded-br-lg bg-[#E8E8E8] text-secondary text-sm {isUploadingFile
            ? 'cursor-not-allowed'
            : 'cursor-pointer'}"
          on:click|preventDefault={() => !isUploadingFile && fileInput.click()}
        >
          Select or drag and drop here
        </button>
        <input
          type="file"
          accept=".pdf, .jpg, .png, .jpeg"
          style="display: none"
          id="fileInput"
          on:change={onFileChange}
          bind:this={fileInput}
        />
      </div>
    {/if}
  </div>
</NotificationModal>
